import { useEffect, useEffectEvent, useRef, useState } from "react";
import { Outlet } from "react-router";
import { useDevices } from "../../shared/devices/devices.ts";
import { groupHistory, useFeedHistory, type FeedFilter } from "../../shared/messages/messages.ts";
import { BottomSheet, useCloseSheet } from "../../shared/ui/BottomSheet/BottomSheet.tsx";
import { Button } from "../../shared/ui/Button/Button.tsx";
import { Spinner } from "../../shared/ui/Spinner/Spinner.tsx";
import { emptyFeedText, FeedFilters, MessageCard } from "./Feed.tsx";
import styles from "./HistorySheet.module.css";

const LOAD_ERROR = "Не удалось загрузить сообщения.";

/**
 * «История сообщений» (07.06 — Filled/Empty, 07.07 — Loading, 07.08 — Load Error): шторка поверх главной
 * с бесконечной лентой, сгруппированной по датам. Календарь из макета вне MVP и не выводится.
 */
export function HistorySheet() {
  const close = useCloseSheet("/feed");
  const [filter, setFilter] = useState<FeedFilter>("all");
  const history = useFeedHistory(filter);
  const devices = useDevices().data ?? [];

  return (
    <>
      <BottomSheet open onClose={close} onBack={close} fill title="История сообщений">
        <FeedFilters value={filter} onChange={setFilter} />
        <div className={styles.scroller}>
          {history.isPending ? (
            <div className={styles.placeholder} role="status" aria-label="Загрузка сообщений">
              <Spinner size={48} />
              <p className={styles.notice}>Загружаем сообщения…</p>
            </div>
          ) : !history.data ? (
            <div className={styles.placeholder}>
              <p className={styles.notice}>{LOAD_ERROR}</p>
              <Button variant="secondary" onClick={() => void history.refetch()}>
                Повторить
              </Button>
            </div>
          ) : history.data.length === 0 ? (
            <div className={styles.placeholder}>
              <p className={styles.notice}>{emptyFeedText(filter)}</p>
            </div>
          ) : (
            <>
              {groupHistory(history.data, filter).map((group) => (
                <section key={group.key} className={styles.group} aria-label={group.title}>
                  <h3 className={styles.date}>{group.title}</h3>
                  <ul className={styles.messages}>
                    {group.messages.map((message) => (
                      <li key={message.id}>
                        <MessageCard
                          message={message}
                          device={devices.find((item) => item.id === message.deviceId)}
                          detailsPath={`/history/messages/${message.id}`}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
              {history.isFetchingNextPage ? (
                <div className={styles.more} role="status" aria-label="Загрузка сообщений">
                  <Spinner size={32} />
                </div>
              ) : history.isFetchNextPageError ? (
                <div className={styles.more}>
                  <p className={styles.notice}>{LOAD_ERROR}</p>
                  <Button variant="secondary" onClick={() => void history.fetchNextPage()}>
                    Повторить
                  </Button>
                </div>
              ) : (
                history.hasNextPage && <EndOfList onReached={() => void history.fetchNextPage()} />
              )}
            </>
          )}
        </div>
      </BottomSheet>
      {/* Шторка деталей сообщения — вложенный маршрут поверх истории. */}
      <Outlet />
    </>
  );
}

/** Невидимая метка конца списка: когда её докрутили до экрана, подгружается следующая страница. */
function EndOfList({ onReached }: { onReached: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const reached = useEffectEvent(onReached);

  useEffect(() => {
    const target = ref.current;
    if (!target) return;
    // Запас в пол-экрана: следующая страница приходит раньше, чем пользователь упрётся в конец.
    const observer = new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && reached(), {
      rootMargin: "0px 0px 50% 0px",
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  return <div ref={ref} className={styles.end} aria-hidden="true" />;
}
